import Logger from '../util/Logger';
import _ from 'lodash';
import DateTime from './DateTime';
import { eventVisibilityTypeFromString } from './EventVisibilityType';
import { eventQuestionTypeFromString } from './EventQuestionType';
import { eventUseCaseFromString } from './EventUseCase';

export default {

  /**
   * Get the id
   * @param event
   * @returns {string} Event id
   */
  getId (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      const id = _.get(event, 'id');
      if (!id) {
        Logger.assert('Event was missing id');
      } else {
        return id;
      }
    }
  },

  isAnonymous (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      return _.get(event, 'anonymous') || '';
    }
  },

  getRosterId (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      return _.get(event, 'roster_id') || '';
    }
  },

  getSuggestedRosterId (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      return _.get(event, 'suggested_roster_id') || '';
    }
  },

  getTitle (event) {
    if (!event) {
      Logger.assert('Cannot call getTitle() without an Event object');
    } else {
      return _.get(event, 'title');
    }
  },

  getLaunchMedia (event) {
    if (!event) {
      Logger.assert('Cannot call getLaunchMedia() without an Event object');
    } else {
      return _.get(event, 'launch_media') || null;
    }
  },

  getName (event) {
    if (!event) {
      Logger.assert('Cannot call getName() without an Event object');
    } else {
      const name = _.get(event, 'name') || '';
      if (!name) {
        Logger.error('Event was missing name', this.getId(event));
        return '';
      } else {
        return name;
      }
    }
  },

  getHero (event) {
    if (!event) {
      Logger.assert('Cannot call getHero() without an Event object');
    } else {
      const hero = _.get(event, 'hero') || null;
      if (!hero) {
        // Logger.error('Event was missing a hero', this.getId(event));
        return null;
      } else {
        return hero;
      }
    }
  },

  getTenantName (event) {
    if (!event) {
      Logger.assert('Cannot call getTenantName() without an Event object');
    } else {
      const tenant = _.get(event, 'tenant_name') || '';
      if (!tenant) {
        Logger.error('Event was missing a tenant name', this.getId(event));
        return '';
      } else {
        return tenant;
      }
    }
  },

  getVisibilityType (event) {
    if (!event) {
      Logger.assert('Cannot call getVisibilityType() without an Event object');
    } else {
      const type = _.get(event, 'visibility_setting');
      if (!type) {
        Logger.error('Event was missing visibility type', this.getId(event));
        return null;
      } else {
        return eventVisibilityTypeFromString(type);
      }
    }
  },

  getQuestionType (event) {
    if (!event) {
      Logger.assert('Cannot call getQuestionType() without an Event object');
    } else {
      const type = _.get(event, 'question_type');
      if (!type) {
        Logger.error('Event was missing question type', this.getId(event));
        return null;
      } else {
        return eventQuestionTypeFromString(type);
      }
    }
  },

  getVotingMode (event) {
    if (!event) {
      Logger.assert('Cannot call getVotingMode() without an Event object');
    } else {
      return _.get(event, 'voting_mode');
    }
  },

  getNotEditable (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      return _.get(event, 'not_editable') || [];
    }
  },

  getUseCase (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      const useCase = _.get(event, 'use_case') || null;
      return eventUseCaseFromString(useCase);
    }
  },

  getNotEditableIncludes (event, what) {
    const notEditable = this.getNotEditable(event);
    return notEditable.indexOf(what) >= 0;
  },

  /**
   * Get the slug if it exists
   * @param event
   * @returns {string|null} slug or null
   */
  getSlug (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      return _.get(event, 'slug') || null;
    }
  },
  getInvitedUsersCount (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      return _.get(event, 'invited_users_count') || null;
    }
  },
  /**
   * Get the collection if it exists
   * @param event
   * @returns {object} MiniCollection or null
   */
  getCollection (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      return _.get(event, 'collection');
    }
  },
  /**
   * Get the collectionId if it exists
   * @param event
   * @returns {string} collectionId or null
   */
  getCollectionId (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      const collection = this.getCollection(event);
      if(collection){
        return this.getId(collection);
      } else {
        return '';
      }
    }
  },
  /**
   * Is this event created by the User
   * @param event
   * @returns {boolean} is user created
   */
  isUserCreated (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      return !!_.get(event, 'user_created');
    }
  },

  /**
   * Is sentiment enabled for this event
   * @param event
   * @returns {boolean} sentiment is enabled you can show the section
   */
  isSentimentEnabled (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      return !!_.get(event, 'sentiment_enabled');
    }
  },

  /**
   * Get the time zone
   * @param event
   * @returns {moment.Moment} Start time in unix seconds
   */
  getTimeZone (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      const zone = _.get(event, 'time_zone');
      if (!zone) {
        Logger.error('event was missing time_zone', this.getId(event));
        return null;
      } else {
        return zone;
      }
    }
  },

  /**
   * Get the start time
   * @param event
   * @returns {moment.Moment} Start time in unix seconds
   */
  getStartTime (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      const startTime = _.get(event, 'schedule.ideas.start_time');
      if (!startTime) {
        Logger.error('event was missing schedule.ideas.start_time', this.getId(event));
        return null;
      } else {
        const time = DateTime.unixSecondsToMoment(startTime);
        if (!time) {
          return null;
        } else {
          const zone = this.getTimeZone(event);
          if (!zone) {
            return time;
          } else {
            return DateTime.applyTimeZone(time, zone);
          }
        }
      }
    }
  },

  /**
   * Get the end time
   * @param event
   * @returns {moment.Moment} End time in unix seconds
   */
  getEndTime (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      const endTime = _.get(event, 'schedule.voting.end_time');
      if (!endTime) {
        Logger.error('event was missing schedule.voting.end_time', this.getId(event));
        return null;
      } else {
        const time = DateTime.unixSecondsToMoment(endTime);
        if (!time) {
          return null;
        } else {
          const zone = this.getTimeZone(event);
          if (!zone) {
            return time;
          } else {
            return DateTime.applyTimeZone(time, zone);
          }
        }
      }
    }
  },

  getPhase (event) {
    if (!event) {
      Logger.assert('Must pass an event object');
    } else {
      return _.get(event, 'phase');
    }
  },

  /**
   * Is this event Open
   * @param event
   * @returns {boolean} Is this event open
   */
  isOpen (event) {
    const now = DateTime.now();
    const startTime = this.getStartTime(event);
    if (!startTime) {
      return false;
    }

    const endTime = this.getEndTime(event);
    if (!endTime) {
      return false;
    }

    return startTime.isBefore(now) && endTime.isAfter(now);
  },

  /**
   * Is this event Closed
   * @param event
   * @returns {boolean} Is this event closed
   */
  isClosed (event) {
    const now = DateTime.now();
    const endTime = this.getEndTime(event);
    if (!endTime) {
      return false;
    }
    return endTime.isBefore(now);
  },

  /**
   * Is this event Upcoming
   * @param event
   * @returns {boolean} Is this event upcoming
   */
  isUpcoming (event) {
    const now = DateTime.now();
    const startTime = this.getStartTime(event);
    if (!startTime) {
      return false;
    }
    return startTime.isAfter(now);
  },

  /**
   * Get share link
   * @param event
   * @returns {string} Share link
   */
  getShareLink (event) {
    if (!event) {
      Logger.assert('Missing event, cannot call getShareLink()', event);
    } else {
      const shortLink = _.get(event, 'short_share_link') || '';
      if (!!shortLink) {
        const noQuery = shortLink.split('?')[0];
        const parts = noQuery.split('/');
        const slug = parts[parts.length - 1] || '';
        if (slug && typeof window !== 'undefined' && window.location && window.location.origin) {
          const normalized = `${window.location.origin}/shorty/${slug}`;
          console.log('[Event.getShareLink] normalizing legacy short link', { slug, shortLink, normalized });
          return normalized;
        }
        console.log('[Event.getShareLink] returning original short link (no window.origin or no slug)', { shortLink });
        return shortLink;
      } else {
        const shareLink = _.get(event, 'share_link') || '';
        if (!!shareLink) {
          console.log('[Event.getShareLink] using share_link fallback', { shareLink });
          return shareLink;
        } else {
          Logger.error('No share link found for event: ', this.getId(event));
          return '';
        }
      }
    }
  }

};
